import { FormConfig, FormFieldConfig } from '@app/shared/forms/dynamic-form/fields.service';
import { Field, FieldNames, FieldType } from '../constants';
import { getFormFieldType } from '@app/origami-api/utils/form.utils';
import { ConfigServiceModel } from '@app/origami-api/services/config.service';
import { FolderManagementServiceModel } from '@app/origami-api/services/folder-management.service';
import { AgencySubmissionModels } from '@app/pages/main/pages/startup-agency-submission/models/agency-submission.models';
import { FrequencySettings } from '@app/ti-components/study-start-up/models/study-start-up.constants';
import { ISearchResultsEntities } from '@app/origami-api/services/index-search.service';
import { UserServiceModel } from '@app/origami-api/services/user.service';
import { filter, isNil, keyBy, map, reduce, trim, unionBy } from 'lodash';
import { parseFormLayout } from '@app/pages/main/pages/quality/pages/quality-settings/pages/form-setting/components/form-layout-editor-modal/components/form-layout-editor/helpers';
import { LayoutSection } from '@app/pages/main/pages/quality/pages/quality-settings/pages/form-setting/components/form-layout-editor-modal/components/form-layout-editor/models';
import User = UserServiceModel.User;
import UploadFile = AgencySubmissionModels.UploadFile;

export enum UserType {
  User = 'U',
  Group = 'G',
  Everyone = 'E',
}

export interface SearchTopic {
  Fields?: TopicField[];
  Id?: number;
  TopicName: string; //trl-8190-dirty hack for ts compiler regarding to incompatibility between SearchTopic&Topic-interfaces
}

export interface TopicACL {
  AccessRight: number;
  IsSystem: boolean;
  UserId: number;
  UserType: UserType;
}

export interface SimpleTopic {
  SiteId?: number;
  TopicId?: number;
  ParentId?: number;
  TopicTypeId?: number;
  CreatedBy?: number;
  CreatedDate?: string;
  UserId?: number;
  TopicName: string;
  TopicFields?: TopicField[];
  TopicACLs?: TopicACL[];
  UploadedDocuments?: UploadFile[];
  LinkedDocuments?: any;
}

export interface Topic extends SimpleTopic {
  CreatedBy?: number;
  CreatedDate?: string;
  LockedBy?: number;
  ACLs?: TopicACL[];
  PublishStatuses?: EtmfPublishStatus[];
  RelatedDocuments?: SimpleTopic[];
  QueryTickets?: FolderManagementServiceModel.QueryTicket[];
  Workflows?: FolderManagementServiceModel.Workflow[];
  WorkflowStatusId?: number;
  ESignatureRequest?: FolderManagementServiceModel.IDocumentESignatureRequest;
  ESignatureStatuses?: FolderManagementServiceModel.IESignatureStatus[];
}

export interface Folder {
  CreatedByName: string;
  CreatedDateTime: string;
  FolderIndexPath: string;
  LastUpdatedByName: string;
  LastUpdatedDateTime: string;
  Topic: Topic;
  TopicACLs: any;
}

export interface MetadataExtractionTemplateFieldMapping {
  DocumentFieldName: string;
  KofaxFieldName: string;
  TemplateId: number;
}

export interface MetadataExtractionTemplate extends Topic {
  KofaxFieldsMapping: MetadataExtractionTemplateFieldMapping[];
}

export interface EtmfPublishStatus {
  Id: number;
  Rooms: EtmfPublishedToRoom[];
}

export interface EtmfPublishedToRoom {
  DefaultErrorMessage: string;
  ErrorCode: number;
  SiteId: number;
  SiteName: string;
}

export interface CountryTopic extends Topic {
  Amendment?: number;
  CountryName: string;
  DataRoomTopicFields: TopicField[];
  DomainTopicFields: TopicField[];
  HasRequiredDocuments?: boolean;
}

export interface InvestigativeSiteTopic extends Topic {
  ApiName?: string;
  CommunicationLogs?: any[];
  InvestigativeSiteName?: any[];
  Description?: string;
  DisplayName?: string;
  GroupId?: number;
  GroupMembers?: any[]; // User
  GroupTypeId?: number;
  InstitutionalReviewBoards?: any[];
  ParentGroupId?: number;
  ParentTopicId?: number;
  AccessRight?: number;
  PrincipalInvestigator?: string;
  SiteNumber?: string;
  Country?: string;
  StatusId?: number;
}

export interface IrbTopic extends Topic {
  Committees: any[]; // TODO
  DataRoomTopicFields?: TopicField[];
  DomainTopicFields?: TopicField[];
  InvSiteTopicFields?: TopicField[];
  HasRequiredDocuments?: boolean;
  MeetingFrequencyEvent?: FrequencySettings;
  SubmissionDeadlineEvent?: FrequencySettings;
  Amendment?: any;
  DomainIrbTopicId?: number;
}

export interface InvestigativeSite {
  CreatedByName: string;
  CreatedDateTime: string;
  LastUpdatedByName: string;
  LastUpdatedDateTime: string;
  InvestigativeSite: InvestigativeSiteTopic;
  InvestigativeSiteBindedToDataRoom: InvestigativeSiteTopic;
  Amendment?: any;
}

export interface InvestigativeSiteSSUOptions {
  Approvers: User[];
  LocalApproversGroupId?: number;
  RegulatoryApprovalRequestSent?: boolean;
  CurrentUserCanActivateReject?: boolean;
  IsEntityFrozen?: boolean;
  IsCurrentUserStartupSpecialist?: boolean;
  PreSubmissionDeadlineDates?: string[];
  SSUFolderId?: number;
  MainIrbDomainTopicId?: number;
}

export interface InboxTopic extends Topic {
  Attachments: TopicAttachment[];
}

export interface TopicAttachment {
  FileId: number;
  FileName: string;
  FileSize: number;
  FileToken: string;
}

export interface TopicField {
  FieldId?: number;
  FieldName: string;
  FieldType: FieldType;
  Fieldvalue?: any; // that's not a case typo!
  FieldValue?: string;
  UpdateReason?: string;
  TimeZoneShift?: number;
  TimeZoneShift2?: number;
  PreserveSpace?: boolean;
}

export interface TopicType {
  CreatedBy: number;
  CreatedDate: Date; // TODO: maybe string?
  Description: string;
  LastUpdated: Date; // TODO: maybe string?
  LastUpdatedBy: number;
  TopicTypeId: number;
  TypeName: string;
}

export interface TmfPublishing {
  RetrieveFromTmf?: boolean;
}

export enum SearchDataType {
  Default = 0,
  Metadata = 1,
  Files = 2,
  All = Metadata | Files,
}

export interface DeviceInfo {
  Device: string;
  OsVersion: string;
  VerificationDate: string;
  VerifiedBy: string;
}

export interface DocumentType extends SimpleTopic {
  UniversalId?: string;
  CategoryId: number;
  ChildDocTypesCount: number;
  FolderId: number;
  ParentId?: number;
  IncludeForAudit?: boolean;
  IncludeInDocumentTrackerReport: boolean;
  DomainDocumentTypeTopicId?: number;
  EventTypes?: {
    DocumentTypeTopicId: number;
    EventTypeTopicId: number;
  }[];
  NamingPattern: string;
  ShortName: string;
  SortOrder: number;
  TeamId?: number;
}

export interface HistoryNote {
  HistoryNoteId: number;
  UpdatedDate: string;
  Topic: string;
  Field: string;
  UpdatedDateString?: string;
  UpdatedById: number;
  UpdatedBy: string;
  OldValue: string;
  NewValue: string;
  UpdateReason: string;
  Action?: string;
}

export interface OnlyOfficeConfig {
  abovePreviewThreshold: boolean;
  callbackUrl: string;
  documentUpdatedCallbackUrl?: string;
  documentTitle: string;
  documentUrl: string;
  key: string;
  onlyOfficeUrl: string;
  timeout: number;
  logoUrl: string;
  rootUrl: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    name: string;
    group?: string;
  };
  lang: string;
  mode: string;
}

export interface TimeZoneInfo {
  Abbr1: string;
  Abbr2: string;
  Offset: string;
  TimeZoneId: number;
  ZoneKey: string;
  IanaZoneName: string;
}

export interface IWithIdentity {
  toId: () => number;
}

export interface TIAutomateFieldScore {
  DocumentId?: number;
  EntryId?: number;
  EntityTopicTypeId: number;
  EntityFieldName?: string;
  EntityFieldValue?: string;
  DisplayValue?: string;
  Score?: number;
  CollectionIndex?: number;
  Confirmed?: boolean;
  IsMatching: boolean;
}

export interface TIAutomateEntry {
  EntryName: string;
  EntryTopicTypeId: number;
  EntryId?: string;
  Fields: TIAutomateFieldScore[];
}

export interface QmsEvidence {
  TopicId?: number;
  Title?: string;
  Url?: string;
  FileToken?: string;
  DocumentId?: number;
  TopicFields?: TopicField[];
  LinkedRecords: number[];
}

export class TopicUtils {
  static getTopicField(topicOrFieldsArray: SimpleTopic | TopicField[] | SearchTopic[], fieldName: string): TopicField {
    if (!fieldName || !topicOrFieldsArray) {
      return null;
    }

    const topicFields = Array.isArray(topicOrFieldsArray)
      ? <TopicField[]>topicOrFieldsArray
      : (<Topic>topicOrFieldsArray).TopicFields || (<any>topicOrFieldsArray)?.Fields;
    // explicit typing was used to avoid tsc errors

    if (!topicFields || topicFields.length < 1) {
      return null;
    }

    for (const topicField of topicFields) {
      if (topicField?.FieldName === fieldName) {
        return topicField;
      }
    }
    return null;
  }

  static getTopicFieldValue(
    topicOrFieldsArray: SimpleTopic | TopicField[],
    fieldName: string,
    asString?: boolean,
  ): any {
    const topicField = this.getTopicField(topicOrFieldsArray, fieldName);
    const topicFieldValue = topicField?.Fieldvalue || topicField?.FieldValue;

    if (asString && topicField && (topicFieldValue || topicFieldValue === '')) {
      return topicFieldValue;
    } else if (topicField && topicFieldValue) {
      return topicField?.FieldType && TopicUtils.getNormalizedFieldValue(topicFieldValue, topicField.FieldType);
    }

    return null;
  }

  static getNormalizedFieldValue(fieldValue, fieldType) {
    if ([FieldType.Number, FieldType.User, FieldType.Contact].includes(fieldType)) {
      return isNil(fieldValue) ? null : +fieldValue;
    }

    if (fieldType === FieldType.Boolean) {
      const castFieldValue = String(fieldValue).toLowerCase();
      //todo: that is fix for case when server is returning "True" || "False" values for topic fieldValue. that should be fixed on the BE-side !!!
      if (castFieldValue === 'true' || castFieldValue === 'false') {
        return castFieldValue === 'true';
      }
      return !!+fieldValue;
    }

    // some boolean settings may have '1'/'0' values stored as text
    if (fieldType === FieldType.Text) {
      if (fieldValue === '1') {
        fieldValue = 1;
      } else if (fieldValue === '0') {
        fieldValue = 0;
      }
    }

    return fieldValue;
  }

  static getTopicFieldDisplayValue(fieldValue, fieldType, fieldFlags) {
    if (fieldType === FieldType.Number) {
      return fieldValue ? +fieldValue : '';
    }
    if (fieldType === FieldType.Boolean) {
      return this.getBooleanTopicFieldDisplayValue(fieldValue, fieldFlags);
    }

    return fieldValue;
  }

  static getBooleanTopicFieldDisplayValue(fieldValue, fieldFlags) {
    if (!fieldFlags) {
      // console.error('For boolean field was not provided fieldFlags-property');
    }
    const isCheckBox = fieldFlags && Field.Is.checkBox(fieldFlags);
    let castFieldValue: string | boolean = fieldValue;
    if (!isNil(fieldValue)) {
      castFieldValue = String(fieldValue).toLowerCase();
      castFieldValue =
        castFieldValue === 'true' || castFieldValue === 'false'
          ? castFieldValue === 'true'
          : fieldValue === ''
            ? ''
            : !!+fieldValue;
    }

    if (isCheckBox) {
      return castFieldValue ? tiStrings.app.misc.yes : '';
    } else {
      return castFieldValue ? tiStrings.app.misc.yes : castFieldValue === false ? tiStrings.app.misc.no : '';
    }
  }

  static getTopicFieldBooleanValue(topicOrFieldsArray: SimpleTopic | TopicField[], fieldName: string): boolean {
    const topicField = this.getTopicField(topicOrFieldsArray, fieldName);
    return !!topicField && !!+topicField.Fieldvalue;
  }

  static getTopicFieldNumberValue(topicOrFieldsArray: SimpleTopic | TopicField[], fieldName: string): number {
    const topicField = this.getTopicField(topicOrFieldsArray, fieldName);
    return topicField && topicField.Fieldvalue ? +topicField.Fieldvalue : null;
  }

  static getTopicFieldStringValue(topicOrFieldsArray: SimpleTopic | TopicField[], fieldName: string): string {
    const topicField = this.getTopicField(topicOrFieldsArray, fieldName);
    return topicField && topicField.Fieldvalue != null ? topicField.Fieldvalue + '' : null;
  }

  static getTopicsFieldsObject(topicOrFieldsArray: TopicField[]) {
    const topicsFieldsObject = {};
    if (topicOrFieldsArray.length) {
      topicOrFieldsArray.forEach((topic) => {
        if (topic.FieldName) {
          topicsFieldsObject[topic.FieldName] = topic.Fieldvalue;
        }
      });
      return topicsFieldsObject;
    }
    return null;
  }

  static setTopicFieldValue(
    topicOrFieldsArray: SimpleTopic | TopicField[],
    fieldName: string,
    fieldValue: any = null,
    fieldType?: FieldType,
  ): boolean {
    const topicField = this.getTopicField(topicOrFieldsArray, fieldName);
    if (topicField) {
      topicField.Fieldvalue = fieldValue;
      return true;
    } else if (fieldType) {
      const topicFields = Array.isArray(topicOrFieldsArray)
        ? <TopicField[]>topicOrFieldsArray
        : (<Topic>topicOrFieldsArray).TopicFields;
      topicFields.push({
        FieldName: fieldName,
        Fieldvalue: fieldValue,
        FieldType: fieldType,
      });
      return true;
    }

    return false;
  }

  static convertFormToFormConfig(form: ConfigServiceModel.Form, defaultForm?: ConfigServiceModel.Form): FormConfig {
    if (!form) {
      return null;
    }

    const { sections, collectionMap, fieldMap } = parseFormLayout(
      defaultForm?.Collections ?? form?.Collections,
      this.buildLayoutFields(form, defaultForm),
    );

    // `parseFormLayout` was fed the default form's layout, so the custom form's Collections carry
    // stage-level title overrides rather than a layout of their own.
    const resolvedSections = isNil(defaultForm?.Collections)
      ? sections
      : this.applySectionTitleOverrides(sections, form.Collections);

    const fields = this.injectDefaultFormData(form.FormFields || form.Fields || [], defaultForm)
      .map((field) => this.formFieldsConfigMapper(field))
      .map((field) => {
        return {
          ...field,
          parent: fieldMap?.[field.fieldName]?.parent ?? null,
        };
      });

    return <FormConfig>{
      formId: form.FormId,
      formName: form.FormName,
      formTypeId: form.FormTypeId,
      topicTypeId: form.TopicTypeId,
      typeId: form.TopicTypeId,
      siteId: form.SiteId,
      lockedByName: form.LockedByName,
      formFields: fields,
      fieldsDependencies: form.FieldsDependencies,
      sections: resolvedSections,
      collectionMap,
    };
  }

  // Layout structure always comes from the default form; a stage-level custom form may only override
  // section titles, matched by Name because CollectionId is recreated on every workflow save.
  private static applySectionTitleOverrides(
    sections: LayoutSection[],
    collections: (ConfigServiceModel.Group | ConfigServiceModel.Section)[],
  ): LayoutSection[] {
    const titles = reduce(
      filter(collections ?? [], (collection) => collection.Type !== ConfigServiceModel.FormCollectionType.Group),
      (acc, collection) => {
        if (trim(collection.Title)) {
          acc[collection.Name] = trim(collection.Title);
        }

        return acc;
      },
      {} as Record<string, string>,
    );

    return map(sections, (section) => (titles[section.name] ? { ...section, title: titles[section.name] } : section));
  }

  private static buildLayoutFields(
    form: ConfigServiceModel.Form,
    defaultForm?: ConfigServiceModel.Form,
  ): ConfigServiceModel.Field[] {
    if (!defaultForm?.Fields) {
      return form.Fields;
    }

    const customFields = form.FormFields || form.Fields || [];
    const defaultFieldMap = keyBy(defaultForm.Fields, 'FieldName');

    // Every field carries its own flags: an id field and its $$..._Name$$ counterpart are two
    // independent fields as far as the form is concerned, and merging them is a concern of the
    // settings editors, not of the rendered form.
    const promoted = new Set(
      map(
        filter(
          customFields,
          (field) => Field.Is.includeInCoding(field) && isNil(defaultFieldMap[field.FieldName]?.CollectionId),
        ),
        'FieldName',
      ),
    );

    const layoutFields = map(defaultForm.Fields, (field) => {
      if (!promoted.has(field.FieldName) || Field.Is.includeInCoding(field)) {
        return field;
      }

      const includedField = { ...field };
      Field.setFlagOn(includedField, Field.Flag.includeInCoding);

      return includedField;
    });

    const customOnlyFields = filter(
      customFields,
      (field) => !defaultFieldMap[field.FieldName] && promoted.has(field.FieldName),
    );

    return [...layoutFields, ...customOnlyFields];
  }

  // we have no keyword lookups and description in the custom form, so let's inject it there from the default form. also used for the SWS related logic
  static injectDefaultFormData(
    fields: ConfigServiceModel.Field[],
    defaultForm?: ConfigServiceModel.Form,
  ): ConfigServiceModel.Field[] {
    if (!defaultForm || !defaultForm.Fields) {
      return fields;
    }

    const result = fields.map((field) => {
      const defaultFormField = defaultForm.Fields.find((f) => f.FieldName === field.FieldName);

      if (defaultFormField) {
        Object.keys(defaultFormField).forEach((prop) => {
          if (!field[prop]) {
            field[prop] = defaultFormField[prop];
          }
        });
      }

      return field;
    });

    return result;
  }

  static parseFieldCategoryIds(value: string): number[] {
    return value?.length > 0 ? value?.split(',')?.map((id) => +id) || [] : [];
  }

  static formFieldsConfigMapper(field: ConfigServiceModel.Field): FormFieldConfig {
    // TODO: add fields localization
    return <FormFieldConfig>{
      fieldName: field.FieldName,
      fieldValue: field.Fieldvalue,
      flags: field.Flags,
      fieldTitle: field.Title || field.DefaultTitle,
      fieldType: getFormFieldType(field),
      description: field.Description,
      categoryIds: TopicUtils.parseFieldCategoryIds(field.CategoryIds),
      sortOrder: field.SortOrder,
      facetSortOrder: field.FacetSortOrder,
      defaultTitle: field.DefaultTitle,
      hasSqlIndex: field.HasSqlIndex,
      options: field.Options,
      isUsedInCustomForms: field.IsUsedInCustomForms,
      relatedTo: field.RelatedTo,
      readOnlyFlags: field.FieldNonChangeableFlags,
      collectionId: field.CollectionId,
      width: field.Width,
      lookupFieldValues: (field.LookupFieldValues || []).map(function (lookupItem) {
        return {
          value: lookupItem.Value,
          name: lookupItem?.Name,
        };
      }),
      keywordSetId: field.KeywordSetId,
      UIData: field.UIData ? JSON.parse(field.UIData) : {}, // TODO: decode HTML-escaped JSON
      showInlineDescription: Field.Is.showInlineDescription(field.Flags),
    };
  }

  static stripFieldName(fieldName: string) {
    return (fieldName || '').replace(/[^\w]/g, '').toLowerCase();
  }

  static getQASpecificClassName(whatever: string) {
    return `qa-${this.stripFieldName(whatever)}`;
  }

  static createUniqueTopicFieldName(topicTypeName: string, fieldName: string): string {
    return `${topicTypeName}_${fieldName.replace(/[ \t-]+/g, '_')}`;
  }

  static convertSearchResultToTopic(value: ISearchResultsEntities): Topic {
    return {
      TopicId: value.Id,
      TopicTypeId: TopicUtils.getTopicFieldValue(value.Fields, FieldNames.Topic.TopicTypeId),
      SiteId: TopicUtils.getTopicFieldValue(value.Fields, FieldNames.Site.SiteIdVirtual),
      ParentId: TopicUtils.getTopicFieldValue(value.Fields, FieldNames.Document.ParentId),
      TopicName: TopicUtils.getTopicFieldValue(value.Fields, FieldNames.Document.Title),
      TopicFields: value.Fields.map((field) => {
        return { ...field, Fieldvalue: field.FieldValue };
      }),
    };
  }

  static unionTopics(a: TopicField[], b: TopicField[]): TopicField[] {
    const aMap = keyBy(a, 'FieldName');
    const bMap = keyBy(b, 'FieldName');
    const rawResult = unionBy(a, b, 'FieldName');

    return rawResult.map((item) => {
      const aValue = aMap[item.FieldName]?.Fieldvalue;
      const bValue = bMap[item.FieldName]?.Fieldvalue;

      return { ...item, Fieldvalue: aValue || bValue };
    });
  }
}
